import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@src/app/store';
import { ArtifactRecipeSource } from '@src/api/deployment/deploymentApi';
import { getRecipeRequiredSecrets } from '@src/app/recipe/utils';
import connectionManager from '@src/app/kemuHub/connectionManager';
import { SerializableServiceInfo, EnvironmentInfo, KemuHubFunctions } from '@kemu-io/hs-types';
import { generateRecipePackage } from '@common/recipeActions/saveRecipe';
import { RecipeType } from '@kemu-io/kemu-types';

const RecipeDeployerServiceName = 'kemu.io.recipeDeployer';
const DeployRecipeEventName = 'deployRecipe';
const MAPPED_ENCRYPTION_KEY_NAME = 'DEPLOYMENT_ENCRYPTION_KEY';
export interface DeploymentState {
  isDeploying: boolean;
  deployError: string | null;
  deploySuccess: boolean;
}

const initialState: DeploymentState = {
  isDeploying: false,
  deployError: null,
  deploySuccess: false,
};

interface DeployRecipePayload {
  environmentId: string;
  replicas: number;
  encryptionContext: string | null;
  encryptionKey: string | null;
  notes?: string;
}

type DeployRecipeConfig = {
  recipeSource: ArtifactRecipeSource;
  environmentId: string;
  replicas: number;
  encryptionSecretName?: string;
  recipeSecrets: string[];
  notes?: string;
  config?: Record<string, any>;
};

/**
 * Helper function to find the recipe deployer service in the list of active services.
 * @param services a list of active services
 * @returns a service info object if the recipe deployer service is found, otherwise null.
 */
const findRecipeDeployerService = (services: SerializableServiceInfo[]): SerializableServiceInfo | null => {
  const deployer = services.find((service) => service.name === RecipeDeployerServiceName && service.internal);
  return deployer || null;
};

export const deployRecipeAction = createAsyncThunk(
  'deployment/deployRecipe',
  async (payload: DeployRecipePayload, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const currentRecipe = state.workspace.currentRecipe;
    const userProfile = state.user.currentUserProfile;

    if (!currentRecipe.poolId || !currentRecipe.uuid || !currentRecipe.entityInfo?.dbId) {
      throw new Error('Invalid recipe state');
    }

    if (!userProfile.profile) {
      console.error('User profile not loaded yet');
      return null;
    }

    const connector = connectionManager.getConnector();
    if (!connector) {
      throw new Error('Hub connector is not available');
    }

    // Get services list to find the recipe deployer service
    const services = await connector.getServices();
    const recipeDeployer = findRecipeDeployerService(services.available);
    
    if (!recipeDeployer) {
      throw new Error('Recipe deployer service is not available');
    }

    // Get recipe required secrets
    const requiredSecretsConfig = getRecipeRequiredSecrets(currentRecipe.poolId);
    const requiredSecretNames = requiredSecretsConfig.map(s => s.name);

    // Generate recipe package
    const { contents, parsedContents, dbInfo } = await generateRecipePackage({
      recipePoolId: currentRecipe.poolId,
      name: currentRecipe.name,
      createNewUuid: false,
      // Save the current user as the author info
      authorInfo: {
        id: userProfile.profile.id,
        firstName: userProfile.profile.firstName,
        lastName: userProfile.profile.lastName || '',
      },
      saveGlobalVariableValues: state.interface.logicMapperSettings.saveGlobalVariableValues,
    });
    
    const isDiskRecipe = currentRecipe.type === RecipeType.Desktop;
    const hasBeenSaved = !!dbInfo || isDiskRecipe;
     
    if (!hasBeenSaved) {
      throw new Error('Recipe has not been saved yet');
    }

    // Build recipe source
    const recipeSource: ArtifactRecipeSource = {
      type: 'artifact',
      recipeUuid: parsedContents.uuid,
      name: currentRecipe.name,
      recipeDbId: dbInfo?.id,
      // NOTE: this will be added by the BE.
      artifactUrl: '',
      recipeBlob: contents,
      version: currentRecipe.currentVersion,
    };

    // Build deployment config
    const deployConfig: DeployRecipeConfig = {
      recipeSource,
      environmentId: payload.environmentId,
      replicas: payload.replicas,
      recipeSecrets: requiredSecretNames,
      config: {},
      ...(payload.encryptionKey && { encryptionSecretName: payload.encryptionKey }),
      ...(payload.notes && { notes: payload.notes }),
    };

    // Map encryption secret to recipe if encryptionSecretName is defined
    if (payload.encryptionKey && payload.encryptionContext) {
      try {
        await connector.executeHubFunction(KemuHubFunctions.AddRecipeSecretMapping, [{
          recipeUuid: parsedContents.uuid,
          secretsMap: {
            [MAPPED_ENCRYPTION_KEY_NAME]: {
              context: payload.encryptionContext,
              envVarName: payload.encryptionKey,
              isOptional: false,
              rememberMapping: false,
            },
          },
        }]);

        // Update the deploy config to point to the mapped encryption secret
        deployConfig.encryptionSecretName = MAPPED_ENCRYPTION_KEY_NAME;
      } catch (error) {
        throw new Error(`Failed to map encryption secret: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Call the hub service
    const result = await connector.callProcessorHandler(
      recipeDeployer.sessionId,
      {} as EnvironmentInfo,
      DeployRecipeEventName,
      deployConfig,
      {
        // Wait for up to 3 minutes for the deployment to complete
        timeout: 1000 * 60 * 3,
      }
    );

    if (result.error) {
      throw new Error(result.error);
    }

    if (result.success && result.success.length > 0) {
      return result.success[0];
    }

    throw new Error('Deployment failed: No deployment returned from service');
  }
);

export const deploymentSlice = createSlice({
  name: 'deployment',
  initialState,
  reducers: {
    resetDeploymentState: (state) => {
      state.isDeploying = false;
      state.deployError = null;
      state.deploySuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(deployRecipeAction.pending, (state) => {
      state.isDeploying = true;
      state.deployError = null;
      state.deploySuccess = false;
    });
    builder.addCase(deployRecipeAction.fulfilled, (state) => {
      state.isDeploying = false;
      state.deploySuccess = true;
    });
    builder.addCase(deployRecipeAction.rejected, (state, action) => {
      state.isDeploying = false;
      state.deployError = action.error.message || 'Failed to deploy recipe';
    });
  }
});

export const { resetDeploymentState } = deploymentSlice.actions;
export default deploymentSlice.reducer;

